import {mapState, mapGetters} from 'vuex';
import config from "./config";
import View from "./view";

export default {
    props: ['id', 'location', 'info', 'parent'],
    render: () => null,

    methods: {
        isAnnotationVisible() {
            const parentWorldMatrix = this.getVolumeWorldMatrix(this.location.branchId);

            if (!parentWorldMatrix) {
                return false;
            }

            const objectPosition = this.location.position.clone().applyMatrix4(parentWorldMatrix);

            const volumeVisibility = this.getVolumeVisibility(this.location.branchId);
            const isInsideClipBox = clipPlaneController.isPointInsideClipBox(objectPosition);
            const modelObject = getModelObject(this.location.modelId);
            const isInsidePointCloud = modelObject?.meshType === MeshTypeEnum.POINT_CLOUD ? pointCloudController.isPointInside(objectPosition) : true;

            return volumeVisibility
                && isInsideClipBox
                && isInsidePointCloud
                && !modelManager.isModelAnimating(this.location.modelId)
                && wv.allVolumesForFaces.visible;
        },

        getAnnotationData() {
            return {
                isDetailsShown: this.isDetailsShown,
                config: this.configuration,
                info: this.info,
                location:this.location,
            };
        },

        updateAnnotation () {
            this.view.resize(this.sizeParams);
            this.view.setConfiguration(this.configuration);
            this.view.setVisibility(this.configuration.visibility);
            this.view.setTooltipOrientation(this.cameraPosition);
        }
    },

    computed: {
        ...mapState('annotation', [
            'isDetailsShown',
            'seeThrough'
        ]),

        ...mapGetters('garbage_can', [
            'cameraPosition',
            'getVolumeWorldMatrix',
            'getVolumeVisibility',
            'sceneSize'
        ]),

        ...mapState({
            reactivityTriggerMatrix: function (state) {
                return state.garbage_can.reactivityTriggers.volume.matrix[this.location.branchId];
            },

            reactivityTriggerVisibility: function (state) {
                return state.garbage_can.reactivityTriggers.volume.visibility[this.location.branchId];
            }
        }),

        color: function () {
            let userId = wv.effectController.ParticipantsColor.hasOwnProperty(this.info.authorId) ? this.info.authorId : wv.registeredID.toString();
            let userColor = wv.effectController.ParticipantsColor[userId];
            let borderColor = {
                r: Math.floor(userColor.r * 255),
                g: Math.floor(userColor.g * 255),
                b: Math.floor(userColor.b * 255),
                a: 1.0
            };

            return {
                line: userColor.getHex(),
                border: borderColor
            }
        },

        length: function () {
            return 0.1
                * Math.min(wv.maxDistanceForTooltipScalling, this.cameraPosition.distanceTo(this.configuration.position))
                * wv.annotationScaleFactor
                * Math.max(1, this.sceneSize / 30);
        },

        sizeParams: function () {
            let arrowLength = this.length * wv.annotationHeadRatio,
                lineLength = this.length * (1 - wv.annotationHeadRatio),
                lineWidth = this.length * 0.008;
            return {
                arrowPositionY: arrowLength / 2,
                arrowScale: new THREE.Vector3(3 * lineWidth, arrowLength, 3 * lineWidth),
                linePositionY: arrowLength + lineLength / 2,
                lineScale: new THREE.Vector3(lineWidth, lineLength, lineWidth),
                tooltipPositionY: this.length
            };
        },

        configuration: function () {
            let volumeMatrix = this.getVolumeWorldMatrix(this.location.branchId);
            volumeMatrix = volumeMatrix ? volumeMatrix : new THREE.Matrix4();

            return {
                position: this.location.position.clone().applyMatrix4(volumeMatrix),
                orientation: new THREE.Quaternion().setFromUnitVectors(
                    config.bindPose,
                    this.location.normal.clone().applyMatrix3(new THREE.Matrix3().setFromMatrix4(volumeMatrix))
                ),
                visibility: this.isAnnotationVisible()
            }
        },

        description: function () {
            if (this.isDetailsShown) {
                let position = this.configuration.position;
                const node = MeshHierarchyViewFeatureData.getDataFromBranchID(this.location.branchId);
                return `Author: ${this.info.authorName}\n${this.info.text}\n${node ? node.name : this.location.branchId}\n` +
                    `X: ${position.x.toFixed(3)} Y: ${position.y.toFixed(3)} Z: ${position.z.toFixed(3)}\n`
            } else {
                return this.info.text;
            }
        },

        visibility: function () {
            return this.isAnnotationVisible();
        }
    },

    watch: {
        cameraPosition: function () {
            this.updateAnnotation();
        },

        sceneSize: function () {
            this.view.resize(this.sizeParams);
            this.view.setTooltipOrientation(this.cameraPosition);
        },

        seeThrough: function () {
            this.view.setSeeThrough(this.seeThrough);
        },

        isDetailsShown: function () {
            this.view.setDetails(this.getAnnotationData());
        },

        'info.text': function () {
            this.view.setDetails(this.getAnnotationData());
        },

        reactivityTriggerMatrix: function () {
            this.updateAnnotation();
        },

        reactivityTriggerVisibility: function () {
            this.updateAnnotation();
        }
    },

    created: function () {
        this.view = new View(this.id, this.color);
        this.parent.add(this.view.object);

        this.view.setConfiguration(this.configuration);
        this.view.resize(this.sizeParams);
        this.view.setText(this.description);
        this.view.setVisibility(this.visibility);
        this.view.setSeeThrough(this.seeThrough);
    },

    destroyed: function () {
        this.parent.remove(this.view.object);
        this.view.dispose();
    },
};
